import { DownloadIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  ListItem,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import { auth } from "../../../../core/auth";
import { env } from "../../../../env";
import { formatErrorResponse } from "../../../../shared/utils/format-response-error";
import {
  downloadCsv,
  UPLOAD_TYPES,
  UploadColumnRequirement,
  UploadRowMode,
  UploadTypeDoc,
} from "./example-files";

// Maps an upload type to its server upload endpoint (relative to the agency
// member path).
const UPLOAD_PATH_BY_KEY: Record<string, string> = {
  AR_NOTES: "upload_ar_notes",
  AUTH_FILE: "process_jobs/auth_file/upload",
};

type UploadResult = {
  jobId: number;
  totalRows: number;
  resolvedRows: number;
  errorRows: number;
  newRows: number;
  updateRows: number;
};

// Each upload type gets its own tab; pass its key to render just that type.
// Falls back to showing every type if no key is given.
export default function UploadTab({ typeKey }: { typeKey?: string }) {
  const types = typeKey ? UPLOAD_TYPES.filter((type) => type.key === typeKey) : UPLOAD_TYPES;
  return (
    <Stack paddingY={2} spacing={6}>
      {types.map((type) => (
        <UploadTypeCard key={type.key} type={type} />
      ))}
    </Stack>
  );
}

const REQUIREMENT_BADGE: Record<UploadColumnRequirement, { label: string; colorScheme: string }> = {
  required: { label: "required", colorScheme: "red" },
  conditional: { label: "conditional", colorScheme: "purple" },
  "one-of": { label: "one of pair", colorScheme: "orange" },
  identifier: { label: "identifier", colorScheme: "blue" },
  optional: { label: "optional", colorScheme: "gray" },
  "n-a": { label: "leave empty", colorScheme: "blackAlpha" },
  "not-supported": { label: "not supported", colorScheme: "blackAlpha" },
};

function UploadTypeCard({ type }: { type: UploadTypeDoc }) {
  const toast = useToast();
  const { api } = useApi();
  const canUpload = auth.isAllowedTo(
    type.key === "AR_NOTES" ? "view_billing_section_ar" : "upload_authorizations"
  );
  const references = useMutation({
    mutationFn: () => api.get("./process_jobs/ar_notes/references", {}),
    onSuccess: (file) => downloadCsv(file.fileName, file.csv),
    onError: (error) =>
      toast({ title: "Download failed", description: formatErrorResponse(error), status: "error" }),
  });
  const queryClient = useQueryClient();

  // A column's requirement usually differs between a row that creates and a
  // row that updates, so the table shows one kind at a time.
  const [rowMode, setRowMode] = React.useState<UploadRowMode>("new");
  const isUpdate = rowMode === "update";

  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<UploadResult> => {
      const uploadPath = UPLOAD_PATH_BY_KEY[type.key];
      if (uploadPath === undefined) {
        throw new Error(`No upload endpoint for ${type.key}`);
      }
      const authState = auth.getAuthOrFail();
      const data = new FormData();
      data.append("file", file, file.name);
      const response = await axios.post<UploadResult>(
        `${env.API_URL}agencies/${authState.data.agency.id}/agency_members/${authState.data.agencyMember.id}/${uploadPath}`,
        data,
        {
          headers: {
            Authorization: `Token ${authState.tokens.authToken}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: (result) => {
      const queued = [
        result.newRows > 0 ? `${result.newRows} new` : null,
        result.updateRows > 0
          ? `${result.updateRows} update${result.updateRows === 1 ? "" : "s"}`
          : null,
      ]
        .filter((part) => part !== null)
        .join(" + ");
      toast({
        title: "File uploaded",
        description: `${result.totalRows} rows: ${queued === "" ? "0" : queued} queued, ${
          result.errorRows
        } with errors.`,
        status: result.errorRows > 0 ? "warning" : "success",
        position: "top-right",
        duration: 8000,
        isClosable: true,
      });
      queryClient.invalidateQueries({ queryKey: ["process_jobs", "jobs"] });
    },
    onError: (error: unknown) => {
      toast({
        title: "Upload failed",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  function handleUpload() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.xlsx";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        uploadMutation.mutate(file);
      }
    };
    input.click();
  }

  return (
    <Box borderRadius="md" borderWidth="1px" padding={5}>
      <Flex align="flex-start" gap={4} justify="space-between" wrap="wrap">
        <Box>
          <Heading size="md">{type.title}</Heading>
          <Text color="gray.600" fontSize="sm" marginTop={1} maxWidth="3xl">
            {type.description}
          </Text>
        </Box>
        <Flex gap={2} wrap="wrap">
          {type.key === "AR_NOTES" && (
            <Button
              isDisabled={!canUpload}
              isLoading={references.isPending}
              leftIcon={<DownloadIcon />}
              size="sm"
              variant="outline"
              onClick={() => references.mutate()}
            >
              Download valid reasons and refs
            </Button>
          )}
          <Button
            leftIcon={<DownloadIcon />}
            size="sm"
            variant="outline"
            onClick={() => downloadCsv(type.fileName, type.csv)}
          >
            Download example
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!canUpload}
            isLoading={uploadMutation.isPending}
            size="sm"
            onClick={handleUpload}
          >
            Upload file
          </Button>
        </Flex>
      </Flex>

      {type.hasRowModes === true && (
        <Flex align="center" gap={3} marginTop={5}>
          <Text color="gray.700" fontSize="sm" fontWeight="semibold">
            Show rules for
          </Text>
          <Text color={isUpdate ? "gray.500" : "blue.600"} fontSize="sm" fontWeight="semibold">
            New authorization
          </Text>
          <Switch
            isChecked={isUpdate}
            size="md"
            onChange={(e) => setRowMode(e.target.checked ? "update" : "new")}
          />
          <Text color={isUpdate ? "blue.600" : "gray.500"} fontSize="sm" fontWeight="semibold">
            Update (Patch Target filled in)
          </Text>
        </Flex>
      )}

      <Box marginTop={4} overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Column</Th>
              <Th>{isUpdate ? "Required to update" : "Required for new"}</Th>
              <Th>Description</Th>
            </Tr>
          </Thead>
          <Tbody>
            {type.columns.map((column) => {
              const requirement = isUpdate ? column.updateRequirement : column.requirement;
              return (
                <Tr key={column.name}>
                  <Td fontFamily="mono" whiteSpace="nowrap">
                    {column.name}
                  </Td>
                  <Td>
                    <Badge colorScheme={REQUIREMENT_BADGE[requirement].colorScheme}>
                      {REQUIREMENT_BADGE[requirement].label}
                    </Badge>
                  </Td>
                  <Td>
                    {isUpdate && column.updateDescription !== undefined
                      ? column.updateDescription
                      : column.description}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {type.rules !== undefined && (
        <Stack marginTop={6} spacing={4}>
          {type.rules
            .filter((section) => section.mode === undefined || section.mode === rowMode)
            .map((section) => (
              <Box key={section.title}>
                <Heading size="sm">{section.title}</Heading>
                <UnorderedList color="gray.700" fontSize="sm" marginTop={2} spacing={1}>
                  {section.items.map((item) => (
                    <ListItem key={item}>{item}</ListItem>
                  ))}
                </UnorderedList>
              </Box>
            ))}
        </Stack>
      )}
    </Box>
  );
}
